import {
  useSnackbar,
  useLocalization,
  useDataProvider,
  SnackbarApiErrorMessage,
  useDialog,
} from '@ui-framework/index';
import { useState } from 'react';
import PermissionButton from './PermissionButton';
import IconLibrary from './IconLibrary';

const getFileName = () => {
  const now = new Date();
  const pad = n => String(n).padStart(2, '0');
  const dateTime = [
    now.getFullYear(),
    pad(now.getMonth() + 1),
    pad(now.getDate()),
    pad(now.getHours()),
    pad(now.getMinutes()),
    pad(now.getSeconds()),
  ].join('');
  return dateTime;
};

function ExportButton({ exportUrl, filePrefix, permissionName, table }) {
  const snackbar = useSnackbar();
  const { formatMessage } = useLocalization();
  const [loading, setLoading] = useState(false);
  const { confirmationRef } = useDialog();

  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { getBlob } = useDataProvider(provider);

  const downloadCSV = async () => {
    try {
      setLoading(true);
      const filename = `${filePrefix}-${getFileName()}.csv`;
      const searchParams = table.current.getFilter();
      searchParams.delete('page');
      searchParams.delete('limit');
      searchParams.delete('sortBy');
      const queryParams = searchParams.toString();
      const url = `${exportUrl}?${queryParams}`;
      const response = await getBlob(url);

      if (response?.ok) {
        // Check response header content-type is json or text
        const contentType = response.headers['content-type'];
        if (contentType && contentType.includes('application/json')) {
          const responseText = await response.data.text();
          const responseJson = JSON.parse(responseText);
          confirmationRef.current.init({
            Icon: <IconLibrary.Export />,
            message: responseJson?.message,
            positiveButtonText: 'Close',
            positiveButtonIcon: <IconLibrary.Close />,
            width: 360,
            title: 'Export Successful',
            onChange: () => {
              confirmationRef.current.setOpen(false);
            },
          });
          confirmationRef.current.setOpen(true);

          return;
        }
        // Try to get filename from response headers
        const disposition = response.headers['content-disposition'];
        let resolvedFilename = filename;

        if (disposition && disposition.includes('filename=')) {
          const match = disposition.match(/filename="?([^"]+)"?/);
          if (match?.[1]) {
            // eslint-disable-next-line prefer-destructuring
            resolvedFilename = match[1];
          }
        }

        const blob = new Blob([response.data], {
          type: response.headers['content-type'] || 'text/csv',
        });

        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = resolvedFilename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(downloadUrl);
        snackbar.showMessage(
          formatMessage(
            {
              id: 'FILE_DOWNLOADED',
              defaultMessage: 'File {fileNmae} was downloaded successfully',
            },
            {
              fileNmae: resolvedFilename,
            },
          ),
        );
      } else {
        snackbar.showMessage({
          type: 'error',
          data: <SnackbarApiErrorMessage errors={response?.errors} />,
        });
      }
    } catch (err) {
      snackbar.showMessage({
        type: 'error',
        data: <SnackbarApiErrorMessage errors={[{ message: err?.message }]} />,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <PermissionButton
      permissionName={permissionName}
      variant="contained"
      color="primary"
      disabled={loading}
      size="small"
      startIcon={<IconLibrary.ServiceJobExport />}
      onClick={() => {
        downloadCSV();
      }}>
      {loading
        ? 'Downloading file...'
        : formatMessage({
            id: 'USER_EXPORT_RECORDS',
            defaultMessage: 'Export',
          })}
    </PermissionButton>
  );
}

export default ExportButton;
